import GenericErrorMessage from '@/components/GenericErrorMessage';
import { useModals } from '@/components/modals/ModalProvider';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import fetcher, { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import { getMasterData } from '@/libs/fleet-api/services/master-data';
import {
	ApprovalListItem,
	getPipelineList,
	submitPipelineCCDH,
} from '@/libs/fleet-api/services/pipeline';
import {
	FileSearchOutlined,
	InfoCircleFilled,
	LoadingOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Flex,
	Form,
	Input,
	Row,
	Select,
	Table,
	notification,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

type CCDHPipelineSummaryPage = {
	prospectId: string;
};

const CCDHPipelineSummaryPage: FC<CCDHPipelineSummaryPage> = () => {
	const { openPipelineModal } = useModals();
	const [checkedPipelineIds, setCheckedPipelineIds] = useState<number[]>([]);
	const [approvalList, setApprovalList] = useState<ApprovalListItem[]>([]);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
		useState<boolean>(false);

	const searchForm = useForm<{
		by: 'period' | 'customer-name' | 'status';
		value: string;
	}>();

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');

	const updateDateValue = (newStartDate: string, newEndDate: string) => {
		const formattedValue = `${newStartDate}|${newEndDate}`;
		searchForm.form.setFieldsValue({ ['value']: formattedValue });
	};

	const { data: prospectGroupStatus } = useQuery({
		queryKey: ['getMasterData'],
		queryFn: () => {
			return getMasterData('prospect-group-status');
		},
	});

	const { data, isFetching, refetch, isError } = useQuery({
		queryKey: ['getPipelineList'],
		queryFn: () => {
			const params: FleetQueryParams = {
				pagination: { pageSize: 20, pageNumber: 1 },
				filter: {
					by: 'plafon-type',
					value: 'NonJoinFinancing',
				},
				...(searchForm.value?.by || searchForm.value?.value
					? {
							search: {
								by: searchForm.value?.by ?? '',
								value: searchForm.value?.value ?? '',
							},
						}
					: {}),
			};
			return getPipelineList(params);
		},
	});

	const dataSource =
		data?.data.items.map((item, key) => {
			return { ...item, key };
		}) ?? [];

	const updateCheckedPipelineIds = (
		pipelineHotProspectId: number,
		isChecked: boolean,
	) => {
		if (isChecked) {
			// Add the ID to the list if it's checked
			setCheckedPipelineIds((prev) => [...prev, pipelineHotProspectId]);
		} else {
			// Remove the ID from the list if it's unchecked
			setCheckedPipelineIds((prev) =>
				prev.filter((id) => id !== pipelineHotProspectId),
			);
		}
	};

	const submitCCDHPipelineMutation = useMutation({
		mutationFn: submitPipelineCCDH,
	});

	const onSubmit = async () => {
		try {
			await submitCCDHPipelineMutation.mutateAsync(
				approvalList.filter((item) => checkedPipelineIds.includes(item.id)),
			);
			refetch();
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const updateNotes = (pipelineId: number, notes: string) => {
		setApprovalList((prevList) => {
			// Check if the item already exists in the list
			const existingItemIndex = prevList.findIndex(
				(item) => item.id === pipelineId,
			);

			if (existingItemIndex !== -1) {
				// Update the notes of the existing item
				const updatedList = [...prevList];
				updatedList[existingItemIndex] = {
					...updatedList[existingItemIndex],
					notes: notes,
				};
				return updatedList;
			} else {
				// Add a new item to the list
				return [
					...prevList,
					{ id: pipelineId, approvalStatus: 2, notes: notes },
				];
			}
		});
	};

	const updateApproval = (pipelineId: number, approval: number) => {
		setApprovalList((prevList) => {
			// Check if the item already exists in the list
			const existingItemIndex = prevList.findIndex(
				(item) => item.id === pipelineId,
			);

			if (existingItemIndex !== -1) {
				// Update the notes of the existing item
				const updatedList = [...prevList];
				updatedList[existingItemIndex] = {
					...updatedList[existingItemIndex],
					approvalStatus: approval,
				};
				return updatedList;
			} else {
				// Add a new item to the list
				return [
					...prevList,
					{ id: pipelineId, approvalStatus: approval, notes: '' },
				];
			}
		});
	};

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Divider />
			<FleetTitle>Summary Pipeline</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={() => refetch()}
				form={searchForm.form}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={'Search By'}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ style: { width: 250 } }}
							name={searchForm.fieldName('by')}
							rules={[formRule.required()]}
						>
							<Select
								placeholder="Search By"
								options={[
									{ label: 'Periode', value: 'period' },
									{ label: 'Nama Customer', value: 'customer-name' },
									{ label: 'Status Pengajuan', value: 'status' },
								]}
								onChange={(value) => {
									searchForm.form.setFieldsValue({ ['value']: '' });
									searchForm.form.setFieldsValue({ ['by']: value });
								}}
								style={{ display: 'block', width: '100%' }}
								onClear={async () => {
									searchForm.form.resetFields();
									await refetch();
								}}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						{searchForm.value?.by === 'period' ? (
							<Flex gap={12}>
								<Form.Item
									name={searchForm.fieldName('value')}
									hidden
									noStyle
									rules={[formRule.required()]}
								/>
								<Form.Item
									name="startDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newStartDate = date.format('YYYY-MM-DD');
											setStartDate(newStartDate);
											updateDateValue(newStartDate, endDate);
										}}
									/>
								</Form.Item>
								<Form.Item
									name="endDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newEndDate = date.format('YYYY-MM-DD');
											setEndDate(newEndDate);
											updateDateValue(startDate, newEndDate);
										}}
									/>
								</Form.Item>
							</Flex>
						) : searchForm.value?.by === 'status' ? (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Select options={prospectGroupStatus?.data} />
							</Form.Item>
						) : (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Input
									placeholder="Search..."
									style={{ display: 'block' }}
								/>
							</Form.Item>
						)}
					</Col>
					<Col>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Divider />

			<Table
				scroll={{ x: 'max-content' }}
				bordered
				dataSource={dataSource}
				loading={isFetching}
				columns={[
					{
						width: 1,
						title: 'No',
						align: 'center',
						render: (_, record) => record.no,
					},
					{
						title: 'Check',
						width: 1,
						align: 'center',
						render: (_, record) => (
							<Checkbox
								onChange={(e) =>
									updateCheckedPipelineIds(
										record.pipelineHotProspectId,
										e.target.checked,
									)
								}
							/>
						),
					},
					{
						title: 'Review Kredit',
						width: 1,
						render: (_, record) => record.creditReview,
					},
					{
						title: 'Rekomendasi Kredit',
						width: 1,
						render: (_, record) => record.creditRecommendation.value,
					},
					{
						title: 'New Order Date',
						width: 100,

						render: (_, record) =>
							dayjs(record.newOrderDate).format('DD-MM-YYYY'),
					},
					{
						title: 'Status Pipeline',
						align: 'center',
						width: 1,

						render: (_, record) => record.pipelineStatus.value,
					},
					{
						title: 'View Detail',
						width: 1,
						align: 'center',
						render: (_, record) => (
							<a
								style={{
									display: 'block',
								}}
								onClick={(e) => {
									e.preventDefault();
									openPipelineModal(record.pipelineHotProspectId);
								}}
							>
								<FileSearchOutlined />
							</a>
						),
					},
					{
						title: 'Final Pipeline',
						width: 200,
						align: 'center',
						render: (_, record) => {
							return (
								<Select
									style={{
										width: 200,
									}}
									options={[
										{
											label: 'Approved',
											value: 2,
										},
										{
											label: 'Reject',
											value: 4,
										},
									]}
									onSelect={(value) =>
										updateApproval(record.pipelineHotProspectId, value)
									}
								/>
							);
						},
					},
					{
						title: 'Notes Pipeline',
						width: 200,
						render: (_, record) => {
							return (
								<Input
									onChange={(e) =>
										updateNotes(record.pipelineHotProspectId, e.target.value)
									}
								/>
							);
						},
					},
				]}
			/>
			<Divider />

			<Flex
				justify="flex-end"
				gap={12}
			>
				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginTop: 30,
					}}
					disabled={!checkedPipelineIds.length}
					onClick={() => setIsConfirmationModalOpen(true)}
				>
					Submit
				</Button>
			</Flex>
			<FleetModal
				open={isConfirmationModalOpen}
				okText="YA"
				onOk={async () => {
					onSubmit();
					setIsConfirmationModalOpen(false);
				}}
				onCancel={() => {
					setIsConfirmationModalOpen(false);
				}}
				cancelText="TIDAK"
			>
				<Flex
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: 200,
						flexDirection: 'column',
						gap: 24,
					}}
				>
					<InfoCircleFilled
						style={{ color: '#1557BC', fontSize: 72 }}
						size={72}
					/>
					<span style={{ fontWeight: 600, fontSize: 16 }}>
						Apakah Anda Yakin Ingin Melakukan Submission Pipeline?
					</span>
				</Flex>
			</FleetModal>
		</Fragment>
	);
};

export default CCDHPipelineSummaryPage;
